import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PaginatedList, PaginatedQueryParams } from "../types/pagination";
import { createSearchParams, hasActiveFilters } from "../utils/searchApiUtils";
import { useInfiniteDataTableItems } from "./useInfiniteDataTableItems";

type DataTableQueryResult<TItem> = {
  currentData?: PaginatedList<TItem>;
  isLoading: boolean;
  isFetching: boolean;
  error?: unknown;
};

interface UseDataTablePaginationOptions<TQueryArg> {
  initialFilters?: Record<string, any>;
  baseFilters?: Record<string, any>;
  buildQueryArg?: (queryParams: PaginatedQueryParams) => TQueryArg;
  mapSearchFilters?: (filters: Record<string, any>) => Record<string, any>;
}

export const useDataTablePagination = <
  TItem,
  TQueryArg = PaginatedQueryParams
>(
  useQueryHook: (queryArg: TQueryArg) => DataTableQueryResult<TItem>,
  pageSize: number,
  options: UseDataTablePaginationOptions<TQueryArg> = {}
) => {
  const { baseFilters, buildQueryArg, mapSearchFilters, initialFilters } =
    options;
  const [page, setPage] = useState(0);
  const [searchFilters, setSearchFilters] = useState<Record<string, any>>(
    () => initialFilters ?? {},
  );
  const baseFiltersKey = useMemo(
    () => JSON.stringify(baseFilters ?? {}),
    [baseFilters]
  );
  const previousBaseFiltersKeyRef = useRef(baseFiltersKey);
  const lastKnownTotalCountRef = useRef<number | undefined>(undefined);

  const effectiveFilters = useMemo(() => {
    const mappedSearchFilters = mapSearchFilters
      ? mapSearchFilters(searchFilters)
      : searchFilters;
    const mergedFilters = {
      ...mappedSearchFilters,
      ...baseFilters,
    };

    return hasActiveFilters(mergedFilters) ? mergedFilters : undefined;
  }, [baseFilters, mapSearchFilters, searchFilters]);

  const queryParams = useMemo(
    () =>
      createSearchParams(
        { pageNumber: page + 1, pageSize },
        effectiveFilters
      ),
    [effectiveFilters, page, pageSize]
  );

  const queryArg = useMemo(
    () =>
      buildQueryArg
        ? buildQueryArg(queryParams)
        : (queryParams as TQueryArg),
    [buildQueryArg, queryParams]
  );

  const { currentData, isLoading, isFetching, error } = useQueryHook(queryArg);
  const { accumulatedItems, resetAccumulatedItems } =
    useInfiniteDataTableItems<TItem>(currentData?.items, page);

  const resetTableItems = useCallback(() => {
    lastKnownTotalCountRef.current = undefined;
    resetAccumulatedItems();
    setPage(0);
  }, [resetAccumulatedItems]);

  useLayoutEffect(() => {
    if (previousBaseFiltersKeyRef.current === baseFiltersKey) {
      return;
    }

    previousBaseFiltersKeyRef.current = baseFiltersKey;
    resetTableItems();
  }, [baseFiltersKey, resetTableItems]);

  useEffect(() => {
    if (currentData?.totalCount !== undefined) {
      lastKnownTotalCountRef.current = currentData.totalCount;
    }
  }, [currentData?.totalCount]);

  const handleSearchChange = useCallback(
    (filters: Record<string, any>) => {
      setSearchFilters(filters);
      resetTableItems();
    },
    [resetTableItems]
  );

  const handleLoadMore = useCallback(() => {
    if (currentData?.hasNextPage && !isFetching) {
      setPage((currentPage) => currentPage + 1);
    }
  }, [currentData?.hasNextPage, isFetching]);

  return {
    items: accumulatedItems,
    isLoading: isLoading && accumulatedItems.length === 0,
    isLoadingRows: isFetching && page === 0 && accumulatedItems.length === 0,
    isLoadingMore: isFetching && page > 0,
    totalCount: currentData?.totalCount ?? lastKnownTotalCountRef.current,
    hasNextPage: currentData?.hasNextPage,
    error,
    page,
    handleSearchChange,
    handleLoadMore,
    resetTableItems,
  };
};
